"use client";

import * as React from "react";
import Link from "next/link";
import { FolderOpen, GripVertical, Users, FileCheck, Mic } from "lucide-react";
import type { DraggableAttributes } from "@dnd-kit/core";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/overlays/tooltip";
import { Badge } from "@/components/ui/data-display/badge";
import { cn } from "@/lib/utils";

import type { Project } from "@/presentation/projects/types/Project";

export type ProjectStatus = Project["status"];

export interface ProjectCardDragHandleProps {
  attributes: DraggableAttributes;
  listeners?: React.HTMLAttributes<HTMLElement>;
  isDragging?: boolean;
}

export interface ProjectCardProps {
  title: string;
  status: ProjectStatus;
  recordsCount: number;
  projectReportsCount?: number;
  activeInterviewRoomsCount?: number;
  projectId?: string;
  onClick?: () => void;
  ownerEmail?: string | null;
  /**
   * When set, only the top header row (icon + status) is the drag target.
   * Icon swaps to GripVertical on header hover.
   */
  dragHandle?: ProjectCardDragHandleProps | null;
  cardRef?: React.Ref<HTMLAnchorElement | HTMLDivElement>;
  style?: React.CSSProperties;
  className?: string;
  /** Floating card rendered inside DragOverlay (glass + backdrop blur). */
  isDragOverlay?: boolean;
}

const STATUS_VARIANTS: Record<
  ProjectStatus,
  { label: string; variant: "default" | "secondary" | "outline" }
> = {
  new: { label: "New", variant: "default" },
  opened: { label: "Opened", variant: "outline" },
  closed: { label: "Closed", variant: "secondary" },
};

export function ProjectCard({
  title,
  status,
  recordsCount,
  projectReportsCount = 0,
  activeInterviewRoomsCount = 0,
  projectId,
  onClick,
  ownerEmail,
  dragHandle = null,
  cardRef,
  style,
  className,
  isDragOverlay = false,
}: ProjectCardProps) {
  const statusConfig = STATUS_VARIANTS[status];
  const isClosed = status === "closed";
  const isDragging = Boolean(dragHandle?.isDragging);
  // Browser synthesizes a click after drag-release on <a>; suppress that navigation.
  const suppressNextClickRef = React.useRef(false);

  React.useEffect(() => {
    if (isDragging) suppressNextClickRef.current = true;
  }, [isDragging]);

  const cardIconClassName = cn(
    "w-4 h-4 transition-colors duration-300",
    isClosed
      ? "text-[var(--icon)]/50 group-hover:text-[var(--icon)]"
      : "text-sidebar-icon-foreground group-hover:text-sidebar-icon-accent-foreground"
  );
  const statIconClassName = cn(
    "w-3 h-3 transition-colors duration-300",
    isClosed
      ? "text-[var(--text-secondary)]/50 group-hover:text-[var(--text-secondary)]"
      : "text-sidebar-icon-foreground group-hover:text-sidebar-icon-accent-foreground"
  );
  const statTextClassName = cn(
    "text-xs transition-colors duration-300",
    isClosed
      ? "text-[var(--text-secondary)]/50 group-hover:!text-[var(--text-secondary)]"
      : "font-[450] text-sidebar-muted-foreground group-hover:text-sidebar-accent-foreground"
  );

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (suppressNextClickRef.current || isDragging) {
      e.preventDefault();
      e.stopPropagation();
      suppressNextClickRef.current = false;
      return;
    }

    // Allow browser default behavior for:
    // - Ctrl/Cmd + click (open in new tab)
    // - Shift + click (open in new window)
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      // Let browser handle it naturally - open in new tab/window
      e.stopPropagation();
      return;
    }
    // For normal clicks, Next.js Link will handle navigation automatically
    // No need to call onClick callback as Link will navigate via href
  };

  const handleAuxClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Handle middle mouse button click - allow default to open in new tab
    if (e.button === 1) {
      // Browser will handle opening in new tab naturally
    }
  };

  const dragListeners = dragHandle?.listeners;
  const {
    onPointerDown: dragPointerDown,
    ...restDragListeners
  } = dragListeners ?? {};

  const leadingIcon = (
    <div className="relative flex items-center justify-center size-5 rounded-lg pointer-events-none">
      <FolderOpen
        className={cn(
          cardIconClassName,
          dragHandle &&
            cn(
              "absolute transition-opacity duration-200",
              "opacity-100 group-hover/header:opacity-0",
              isDragging && "opacity-0",
            ),
        )}
        aria-hidden="true"
      />
      {dragHandle ? (
        <GripVertical
          className={cn(
            "w-4 h-4 absolute transition-opacity duration-200",
            isClosed
              ? "text-[var(--icon)]/50 group-hover:text-[var(--icon)]"
              : "text-sidebar-icon-foreground group-hover:text-sidebar-icon-accent-foreground",
            "opacity-0 group-hover/header:opacity-100",
            isDragging && "opacity-100",
          )}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );

  const headerRow = (
    <div
      className={cn(
        "flex items-center justify-between gap-4",
        dragHandle &&
          "group/header cursor-grab active:cursor-grabbing touch-none",
      )}
      {...(dragHandle
        ? {
            ...dragHandle.attributes,
            ...restDragListeners,
            onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
              // Keep Link from treating the header press as navigation intent.
              event.stopPropagation();
              dragPointerDown?.(event);
            },
            onClick: (event: React.MouseEvent<HTMLDivElement>) => {
              // Header is for drag only; don't navigate via accidental header click.
              event.preventDefault();
              event.stopPropagation();
            },
            role: "button",
            "aria-label": "Drag to reorder",
            tabIndex: 0,
          }
        : {})}
    >
      {leadingIcon}
      {status === "new" && (
        <Badge
          variant="default"
          className="bg-[var(--brand)]/10 text-semibold text-[var(--brand)] border-transparent rounded-full px-3 py-1 group-hover:bg-[var(--brand)] group-hover:text-white pointer-events-none"
          style={{
            transition: "background-color 250ms ease-in-out, color 300ms ease-in-out, transform 75ms ease-in-out",
          }}
        >
          {statusConfig.label}
        </Badge>
      )}
      {status === "opened" && (
        <Badge
          variant="outline"
          className="bg-[var(--status-info-text)]/10  text-[var(--status-info-text)] border-transparent rounded-full px-3 py-1 group-hover:bg-[var(--status-info-text)] group-hover:text-white pointer-events-none"
          style={{
            transition: "background-color 250ms ease-in-out, color 300ms ease-in-out, transform 75ms ease-in-out",
          }}
        >
          {statusConfig.label}
        </Badge>
      )}
      {status === "closed" && (
        <Badge
          variant={statusConfig.variant}
          className="bg-[var(--background-hover)]/80 text-[var(--text-disabled)]/90 border-none rounded-full px-3 py-1 group-hover:text-[var(--text)]/80 pointer-events-none"
          style={{
            transition: "background-color 250ms ease-in-out, color 300ms ease-in-out, transform 75ms ease-in-out",
          }}
        >
          {statusConfig.label}
        </Badge>
      )}
      {dragHandle ? <span className="sr-only">Drag to reorder</span> : null}
    </div>
  );

  const cardContent = (
    <>
      {headerRow}

      <div className="flex-1 mt-4">
        <h3
          className={cn(
            "text-md transition-colors duration-300",
            isClosed
              ? "font-medium text-[var(--text-secondary)]/50 group-hover:text-[var(--text-secondary)]"
              : "font-[450] text-[var(--report-widget-strong-text)] group-hover:text-sidebar-accent-foreground"
          )}
        >
          {title}
        </h3>
      </div>

      <div className="flex flex-col gap-2 mt-0 pt-4 border-t border-transparent">
        <div className="flex items-center gap-2">
          <Tooltip delayDuration={250}>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "flex items-center shadow-xs gap-1.5 px-2.5 py-1 rounded-full dark:bg-[var(--background-hover)] bg-[var(--background-custom)]",
                  recordsCount === 0 && "opacity-30",
                )}
              >
                <Users className={statIconClassName} />
                <span className={statTextClassName}>{recordsCount}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="z-[200]">
              Records
            </TooltipContent>
          </Tooltip>
          <Tooltip delayDuration={250}>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "flex items-center shadow-xs gap-1.5 px-2.5 py-1 rounded-full dark:bg-[var(--background-hover)] bg-[var(--background-custom)] text-xs",
                  projectReportsCount === 0 && "opacity-30",
                )}
              >
                <FileCheck className={statIconClassName} />
                <span className={statTextClassName}>{projectReportsCount}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="z-[200]">
              Project Reports
            </TooltipContent>
          </Tooltip>
          <Tooltip delayDuration={250}>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  "flex items-center shadow-xs gap-1.5 px-2.5 py-1 rounded-full dark:bg-[var(--background-hover)] bg-[var(--background-custom)] text-xs",
                  activeInterviewRoomsCount === 0 && "opacity-30",
                )}
              >
                <Mic className={statIconClassName} />
                <span className={statTextClassName}>
                  {activeInterviewRoomsCount}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="z-[200]">
              Active interview links
            </TooltipContent>
          </Tooltip>
        </div>
        {ownerEmail && (
          <div className="flex items-center min-w-0 w-full">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--background-custom)]/50 text-xs min-w-0 max-w-full overflow-hidden text-muted-foreground/80">
              <span className="truncate">{ownerEmail}</span>
            </div>
          </div>
        )}
      </div>
    </>
  );

  const cardClassName = cn(
    `
    group relative flex flex-col justify-between
    rounded-3xl border p-4
    shadow-sm cursor-pointer hover:shadow-md
    ${
      isClosed
        ? "bg-[var(--background-hover)]/20 border-transparent  hover:border-[var(--border-secondary)]/40"
        : "bg-background/35 dark:bg-card/80 border-transparent hover:border-[var(--border-secondary)]/30"
    }
    min-h-[200px]
    w-full
  `,
    // Source item stays as a faint placeholder; blur lives on DragOverlay only
    // (opacity on the same node disables backdrop-filter in Chromium).
    isDragging &&
      !isDragOverlay &&
      "opacity-20 shadow-none border-dashed border-[var(--border-secondary)]",
    isDragOverlay &&
      "pointer-events-none cursor-grabbing shadow-xl border-[var(--border-secondary)]/25 bg-background/55 dark:bg-card/25 backdrop-blur-xl backdrop-saturate-100",
    className,
  );

  const cardStyle: React.CSSProperties = {
    transition:
      "background-color 250ms ease-in-out, color 300ms ease-in-out, transform 75ms ease-in-out, border-color 250ms ease-in-out, box-shadow 250ms ease-in-out",
    ...style,
  };

  // Overlay must stay a non-navigating div; Link is for the grid item only.
  if (projectId && !isDragOverlay) {
    return (
      <Link
        ref={cardRef as React.Ref<HTMLAnchorElement>}
        href={`/space/projects/${projectId}`}
        prefetch={false}
        onClick={handleClick}
        onAuxClick={handleAuxClick}
        className={cardClassName}
        style={cardStyle}
      >
        {cardContent}
      </Link>
    );
  }

  // Otherwise, use div with onClick (fallback for backward compatibility)
  return (
    <div
      ref={cardRef as React.Ref<HTMLDivElement>}
      onClick={onClick}
      className={cardClassName}
      style={cardStyle}
    >
      {cardContent}
    </div>
  );
}
